/**
 * Percent-space geometry primitives (ARCHITECTURE.md §4.0).
 *
 * Everything the editor and the player compute with is a percent of the
 * plate: `x` in `0..100` of the width, `y` in `0..100` of the height. The
 * canvas draws into a 16:9 viewBox (`0 0 100 56.25`) so strokes and handles
 * stay round; `pctToView` / `viewToPct` are the only places the aspect
 * factor appears.
 */

/** Percent of the containing box, `0..100` (§4.0). */
export type Pct = number
export type Pt = [Pct, Pct]
export type Ring = Pt[]
/** A line segment in percent space. */
export type Seg = [Pt, Pt]
export interface Poly {
  outer: Ring
  holes?: Ring[]
}

/** Axis-aligned box in percent space. */
export interface Bbox {
  x0: number
  y0: number
  x1: number
  y1: number
}

/** Width of the SVG viewBox (percent of the plate width). */
export const VIEW_W = 100
/** Height of the SVG viewBox: 16:9, so `y_view = y_pct * ASPECT`. */
export const VIEW_H = 56.25
/** `VIEW_H / 100` — multiply a percent-y to get a viewBox-y. */
export const ASPECT = VIEW_H / 100

export const pctToView = (p: Pt): Pt => [p[0], p[1] * ASPECT]
export const viewToPct = (p: Pt): Pt => [p[0], p[1] / ASPECT]

export function bboxOfRing(ring: Ring): Bbox {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const [x, y] of ring) {
    if (x < x0) x0 = x
    if (y < y0) y0 = y
    if (x > x1) x1 = x
    if (y > y1) y1 = y
  }
  if (!Number.isFinite(x0)) return { x0: 0, y0: 0, x1: 0, y1: 0 }
  return { x0, y0, x1, y1 }
}

export function bboxOfPoly(poly: Poly): Bbox {
  return bboxOfRing(poly.outer)
}

export function bboxUnion(a: Bbox, b: Bbox): Bbox {
  return { x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) }
}

export function bboxContains(b: Bbox, p: Pt, pad = 0): boolean {
  return p[0] >= b.x0 - pad && p[0] <= b.x1 + pad && p[1] >= b.y0 - pad && p[1] <= b.y1 + pad
}

export function bboxIntersects(a: Bbox, b: Bbox): boolean {
  return a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1
}

export const dist = (a: Pt, b: Pt): number => Math.hypot(a[0] - b[0], a[1] - b[1])
export const add = (a: Pt, b: Pt): Pt => [a[0] + b[0], a[1] + b[1]]
export const sub = (a: Pt, b: Pt): Pt => [a[0] - b[0], a[1] - b[1]]
export const scale = (a: Pt, k: number): Pt => [a[0] * k, a[1] * k]
export const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
export const midpoint = (a: Pt, b: Pt): Pt => lerp(a, b, 0.5)
export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
export const ptEq = (a: Pt, b: Pt, eps = 1e-9): boolean => Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps
